import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, FormInput, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import { companyApi } from '../services/api';
import { useCustomerInvite } from '../hooks/useCustomerInvite';

/**
 * Bjud in beställare (ClickUp 869fj44u8): the form on the left, and on the
 * right what happens next and what the client receives, so the contractor
 * knows what they hand over the client's details for.
 */
export function CustomerInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { form, errors, isInviting, result, error, handleChange, handleSubmit, reset } = useCustomerInvite();
  const [companyName, setCompanyName] = useState('');
  const [urlCopied, setUrlCopied] = useState(false);
  const layoutRef = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    companyApi.getCompany().then(company => setCompanyName(company?.companyName ?? '')).catch(() => {});
  }, []);

  // On a phone the send button is far down: bring the confirmation (or the
  // empty form after "Bjud in en till") into view.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    layoutRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [result]);

  // The sender as the mail names it (API invitation-mail-text): the subject
  // has the person, or the company when the names are the same; the text has
  // "person från företag". The same name twice is never shown.
  const person = user?.fullName?.trim() ?? '';
  const distinctPerson = person !== '' && person.toLowerCase() !== companyName.trim().toLowerCase();
  const inviter = distinctPerson ? person : companyName || person || t('customerInvite.page.senderFallback');
  const sender = distinctPerson && companyName
    ? t('customerInvite.page.sender', { person, company: companyName })
    : inviter;

  const copyUrl = () => {
    if (!result?.registrationUrl) return;
    navigator.clipboard.writeText(result.registrationUrl).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const fieldError = (name: string) => (errors[name] ? t(errors[name]) : undefined);
  const doneTitle = result
    ? t(`customerInvite.page.done.${result.outcome === 'invited' && !result.emailSent ? 'created' : result.outcome}`)
    : '';

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('customerInvite.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('customers.title'), path: '/customers' },
            { label: t('customerInvite.title') }
          ]} />
        </div>
      </div>

      <div className="customer-invite-layout" ref={layoutRef}>
        {result ? (
          <Card title={doneTitle} icon="check-circle" className="mb-0" >
            <div className="alert alert-success" role="status">{result.message}</div>
            {result.outcome === 'invited' && (
              <p>{t('customerInvite.page.done.waiting', { name: result.customerName })}</p>
            )}
            {result.registrationUrl && (
              <div className="customer-invite-share">
                <label className="form-label fw-semibold" htmlFor="customer-invite-url">{t('customerInvite.urlLabel')}</label>
                <div className="input-group">
                  <input id="customer-invite-url" type="text" className="form-control" value={result.registrationUrl} readOnly />
                  <button className="btn btn-outline-primary" type="button" onClick={copyUrl}>
                    <i className={`feather-${urlCopied ? 'check' : 'copy'} me-1`} aria-hidden="true"></i>
                    {urlCopied ? t('customerInvite.urlCopied') : t('common.copy')}
                  </button>
                </div>
              </div>
            )}
            <div className="customer-invite-actions">
              <Button type="button" onClick={reset}>
                <i className="feather-user-plus me-2" aria-hidden="true"></i>
                {t('customerInvite.page.done.another')}
              </Button>
              <Button type="button" variant="outline-primary" onClick={() => navigate('/customers/invite-status')}>
                <i className="feather-mail me-2" aria-hidden="true"></i>
                {t('customerInvite.page.done.list')}
              </Button>
            </div>
          </Card>
        ) : (
          <Card title={t('customerInvite.page.details')} icon="user-plus" className="mb-0">
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            <form onSubmit={handleSubmit} noValidate>
              <FormInput
                id="invite-customer-name"
                name="customerName"
                label={t('customerInvite.customerName')}
                value={form.customerName}
                onChange={handleChange}
                placeholder={t('customerInvite.customerNamePlaceholder')}
                helpText={t('customerInvite.page.nameHelp')}
                error={fieldError('customerName')}
                autoComplete="off"
                required
              />
              <FormInput
                name="customerEmail"
                type="email"
                label={t('customerInvite.customerEmail')}
                value={form.customerEmail}
                onChange={handleChange}
                placeholder={t('customerInvite.page.emailPlaceholder')}
                helpText={t('customerInvite.page.emailHelp')}
                error={fieldError('customerEmail')}
                autoComplete="off"
                required
              />
              <FormInput
                type="tel"
                name="customerPhone"
                label={t('customerInvite.page.phone')}
                value={form.customerPhone}
                onChange={handleChange}
                placeholder={t('customerInvite.customerPhonePlaceholder')}
                helpText={t('customerInvite.page.phoneHelp')}
                error={fieldError('customerPhone')}
                autoComplete="off"
              />
              {/* The same text for every invitation, never whether the address has an account (869fmx898). */}
              <p className="small text-muted mb-3" data-testid="invite-existing-account-hint">{t('customerInvite.existingAccountHint')}</p>
              <div className="customer-invite-actions">
                <Button type="submit" isLoading={isInviting}>
                  <i className="feather-send me-2" aria-hidden="true"></i>
                  {t('customerInvite.page.submit')}
                </Button>
                <span className="customer-invite-required">{t('customerInvite.page.required')}</span>
              </div>
            </form>
          </Card>
        )}

        <div className="customer-invite-aside">
          <Card title={t('customerInvite.page.steps.title')} icon="map" className="mb-0">
            <ol className="customer-invite-steps">
              {(['mail', 'bankid', 'list'] as const).map(step => (
                <li key={step}>
                  <p className="customer-invite-step-title">{t(`customerInvite.page.steps.${step}.title`)}</p>
                  <p className="customer-invite-step-text">{t(`customerInvite.page.steps.${step}.text`, { sender })}</p>
                </li>
              ))}
            </ol>
          </Card>

          <Card title={t('customerInvite.page.preview.title')} icon="mail" className="mb-0">
            <div className="customer-invite-mail" aria-label={t('customerInvite.page.preview.label')}>
              <p className="customer-invite-mail-subject">{t('customerInvite.page.preview.subject', { inviter })}</p>
              <p>{form.customerName.trim()
                ? t('customerInvite.page.preview.greeting', { name: form.customerName.trim() })
                : t('customerInvite.page.preview.greetingNoName')}</p>
              <p>{t('customerInvite.page.preview.body', { sender })}</p>
              <span className="customer-invite-mail-button">{t('customerInvite.page.preview.action')}</span>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
